import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import './index.css';
import {
  GET_GLOBAL_EXPANSION_DATA,
  UPDATE_GLOBAL_EXPANSION_DATA,
  DELETE_GLOBAL_EXPANSION_DATA,
} from '../../api/globalExpansion.js';

const STATUS_OPTIONS = [
  'Pending',
  'Under Review',
  'Contacted',
  'Accepted',
  'Rejected',
];

const STAGE_OPTIONS = [
  'Pre-revenue',
  'Early revenue',
  'Growth',
  'Scale',
  'Enterprise',
];

const SECTOR_OPTIONS = [
  'B2B SaaS',
  'Fintech',
  'Healthtech',
  'Consumer / Commerce',
  'Enterprise Software',
  'Other',
];

const GlobalExpansionAdminView = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // View modal state
  const [viewItem, setViewItem] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  // Edit modal state
  const [editItem, setEditItem] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete modal state
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(GET_GLOBAL_EXPANSION_DATA);
      if (res.data && Array.isArray(res.data.data)) {
        setData(res.data.data);
      } else if (Array.isArray(res.data)) {
        setData(res.data);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error('Error fetching Global Expansion plans:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtered list
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        searchTerm === '' ||
        (item.referenceCode && item.referenceCode.toLowerCase().includes(q)) ||
        (item.founderName && item.founderName.toLowerCase().includes(q)) ||
        (item.companyName && item.companyName.toLowerCase().includes(q)) ||
        (item.workEmail && item.workEmail.toLowerCase().includes(q)) ||
        (item.sector && item.sector.toLowerCase().includes(q)) ||
        (item.priorityMarket && item.priorityMarket.toLowerCase().includes(q)) ||
        (item.headquarters && item.headquarters.toLowerCase().includes(q));

      const matchesStatus =
        statusFilter === 'All' || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = data.length;
    const pending = data.filter((d) => d.status === 'Pending' || !d.status).length;
    const underReview = data.filter((d) => d.status === 'Under Review').length;
    const accepted = data.filter((d) => d.status === 'Accepted').length;
    return { total, pending, underReview, accepted };
  }, [data]);

  // Handlers for View
  const handleOpenView = (item) => {
    setViewItem(item);
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setViewItem(null);
  };

  // Handlers for Edit
  const handleOpenEdit = (item) => {
    setEditItem({
      _id: item._id,
      founderName: item.founderName || '',
      workEmail: item.workEmail || '',
      companyName: item.companyName || '',
      website: item.website || '',
      headquarters: item.headquarters || '',
      sector: item.sector || '',
      stage: item.stage || '',
      priorityMarket: item.priorityMarket || '',
      expansionObjective: item.expansionObjective || '',
      availableBudget: item.availableBudget || '',
      budgetCurrency: item.budgetCurrency || 'USD',
      expansionOwner: item.expansionOwner || '',
      status: item.status || 'Pending',
      adminNotes: item.adminNotes || '',
    });
    setEditError('');
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditItem(null);
    setEditError('');
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    setEditLoading(true);
    setEditError('');

    try {
      const updatePayload = {
        founderName: editItem.founderName,
        workEmail: editItem.workEmail,
        companyName: editItem.companyName,
        website: editItem.website,
        headquarters: editItem.headquarters,
        sector: editItem.sector,
        stage: editItem.stage,
        priorityMarket: editItem.priorityMarket,
        expansionObjective: editItem.expansionObjective,
        availableBudget: editItem.availableBudget,
        budgetCurrency: editItem.budgetCurrency,
        expansionOwner: editItem.expansionOwner,
        status: editItem.status,
        adminNotes: editItem.adminNotes,
      };

      const res = await axios.patch(
        `${UPDATE_GLOBAL_EXPANSION_DATA}/${editItem._id}`,
        updatePayload
      );

      if (res.data && res.data.data) {
        setData((prev) =>
          prev.map((d) => (d._id === editItem._id ? res.data.data : d))
        );
        handleCloseEdit();
      }
    } catch (err) {
      console.error('Error updating Global Expansion plan:', err);
      if (err.response && err.response.data && err.response.data.details) {
        setEditError(
          Array.isArray(err.response.data.details)
            ? err.response.data.details.join(', ')
            : err.response.data.details
        );
      } else {
        setEditError('Failed to update plan. Please check inputs.');
      }
    } finally {
      setEditLoading(false);
    }
  };

  // Handlers for Delete
  const handleOpenDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setDeleteId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;

    try {
      await axios.delete(`${DELETE_GLOBAL_EXPANSION_DATA}/${deleteId}`);
      setData((prev) => prev.filter((d) => d._id !== deleteId));
      handleCloseDelete();
    } catch (err) {
      console.error('Error deleting Global Expansion plan:', err);
      alert('Failed to delete application. Please try again.');
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (filteredData.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = [
      'Reference Code',
      'Submission Date',
      'Status',
      'Founder Name',
      'Work Email',
      'Company Name',
      'Website',
      'Headquarters',
      'Sector',
      'Stage',
      'Business Model',
      'Home Market Revenue',
      'Priority Market',
      'Additional Markets',
      'Objective',
      'Timeline',
      'Target Customer',
      'Entry Model',
      'Available Budget',
      'Operating Runway',
      'Expansion Owner',
      'Support Workstreams',
      'Preferred Format',
      'Desired Start Date',
      'First 90-Day Outcome',
      'Documents Count',
      'Admin Notes',
    ];

    const rows = filteredData.map((d) => [
      d.referenceCode || '',
      d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '',
      d.status || 'Pending',
      d.founderName || '',
      d.workEmail || '',
      d.companyName || '',
      d.website || '',
      d.headquarters || '',
      d.sector || '',
      d.stage || '',
      d.businessModel || '',
      `${d.currency || 'USD'} ${d.homeMarketRevenue || ''}`,
      d.priorityMarket || '',
      Array.isArray(d.additionalMarkets) ? d.additionalMarkets.join('; ') : '',
      d.expansionObjective || '',
      d.desiredEntryTimeline || '',
      d.targetCustomer || '',
      d.entryModel || '',
      `${d.budgetCurrency || 'USD'} ${d.availableBudget || ''}`,
      d.runwayMonths || '',
      d.expansionOwner || '',
      Array.isArray(d.supportWorkstreams) ? d.supportWorkstreams.join('; ') : '',
      d.engagementFormat || '',
      d.desiredStartDate || '',
      (d.first90DayOutcome || '').replace(/"/g, '""'),
      (d.documents && d.documents.length) || 0,
      (d.adminNotes || '').replace(/"/g, '""'),
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,\uFEFF' +
      [
        headers.map((h) => `"${h}"`).join(','),
        ...rows.map((r) => r.map((c) => `"${c}"`).join(',')),
      ].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `global_expansion_plans_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'Pending':
        return 'chip-status chip-status-pending';
      case 'Under Review':
        return 'chip-status chip-status-under-review';
      case 'Contacted':
        return 'chip-status chip-status-contacted';
      case 'Accepted':
        return 'chip-status chip-status-accepted';
      case 'Rejected':
        return 'chip-status chip-status-rejected';
      default:
        return 'chip-status chip-status-pending';
    }
  };

  return (
    <Box className="ge-admin-wrapper">
      {/* Page Header */}
      <Box className="ge-admin-header">
        <Box>
          <Typography variant="h4" className="ge-page-title">
            Global Expansion Plans
          </Typography>
          <Typography variant="body2" className="ge-page-subtitle">
            Manage and assess international expansion plans, target markets, and readiness submissions.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            className="ge-btn-refresh"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCSV}
            className="ge-btn-export"
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} className="ge-stats-grid">
        <Grid item xs={12} sm={6} md={3}>
          <Box className="ge-stat-card">
            <Box className="ge-stat-icon stat-icon-total">
              <LanguageIcon />
            </Box>
            <Box>
              <Box className="ge-stat-value">{stats.total}</Box>
              <Box className="ge-stat-label">Total Expansion Plans</Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="ge-stat-card">
            <Box className="ge-stat-icon stat-icon-pending">
              <HourglassEmptyIcon />
            </Box>
            <Box>
              <Box className="ge-stat-value">{stats.pending}</Box>
              <Box className="ge-stat-label">Pending Review</Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="ge-stat-card">
            <Box className="ge-stat-icon stat-icon-review">
              <BusinessIcon />
            </Box>
            <Box>
              <Box className="ge-stat-value">{stats.underReview}</Box>
              <Box className="ge-stat-label">Under Active Review</Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="ge-stat-card">
            <Box className="ge-stat-icon stat-icon-accepted">
              <CheckCircleOutlineIcon />
            </Box>
            <Box>
              <Box className="ge-stat-value">{stats.accepted}</Box>
              <Box className="ge-stat-label">Accepted / Prepared</Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Search & Filter Bar */}
      <Box className="ge-filter-bar">
        <TextField
          size="small"
          placeholder="Search by company, founder, email, market, sector..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="ge-search-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
            Status:
          </Typography>
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150, backgroundColor: '#fff', borderRadius: 1 }}
          >
            <MenuItem value="All">All Statuses</MenuItem>
            {STATUS_OPTIONS.map((st) => (
              <MenuItem key={st} value={st}>
                {st}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {/* Main Table */}
      <TableContainer component={Paper} className="ge-table-paper">
        <Table sx={{ minWidth: 950 }}>
          <TableHead className="ge-table-head">
            <TableRow>
              <TableCell className="ge-head-cell">Ref / Date</TableCell>
              <TableCell className="ge-head-cell">Company & Founder</TableCell>
              <TableCell className="ge-head-cell">Priority Market</TableCell>
              <TableCell className="ge-head-cell">Sector & Stage</TableCell>
              <TableCell className="ge-head-cell">Budget / Revenue</TableCell>
              <TableCell className="ge-head-cell">Status</TableCell>
              <TableCell className="ge-head-cell" align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={36} sx={{ color: '#5c4eb4' }} />
                  <Typography variant="body2" sx={{ mt: 1.5, color: '#64748b' }}>
                    Loading global expansion plans...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" sx={{ color: '#475569', fontWeight: 600 }}>
                    No global expansion plans found
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                    {searchTerm || statusFilter !== 'All'
                      ? 'Try adjusting your search terms or filters.'
                      : 'New applications will appear here as founders submit them.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row) => (
                <TableRow key={row._id} className="ge-table-row">
                  {/* Ref & Date */}
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#5c4eb4' }}>
                      {row.referenceCode || 'GE-DRAFT'}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>
                      {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>

                  {/* Company & Founder */}
                  <TableCell>
                    <Box className="company-col-title">{row.companyName || 'Unnamed Company'}</Box>
                    <Box className="company-col-founder">
                      {row.founderName || 'Founder'} ·{' '}
                      <span className="text-email">{row.workEmail}</span>
                    </Box>
                    <Box className="company-col-location">📍 {row.headquarters || 'Location'}</Box>
                  </TableCell>

                  {/* Priority Market */}
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>
                      🌐 {row.priorityMarket || 'Not specified'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5 }}>
                      Goal: {row.expansionObjective || 'N/A'}
                    </Typography>
                    {row.additionalMarkets && row.additionalMarkets.length > 0 && (
                      <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                        + {row.additionalMarkets.slice(0, 2).join(', ')}
                        {row.additionalMarkets.length > 2 ? ` (+${row.additionalMarkets.length - 2})` : ''}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Sector & Stage */}
                  <TableCell>
                    <Box className="badge-sector">{row.sector || 'General'}</Box>
                    <Box className="text-stage">Stage: {row.stage || 'Growth'}</Box>
                  </TableCell>

                  {/* Budget & Revenue */}
                  <TableCell>
                    <Box className="target-val">
                      Budget: {row.budgetCurrency || 'USD'} {row.availableBudget ? parseInt(row.availableBudget, 10).toLocaleString() : 'N/A'}
                    </Box>
                    <Box className="budget-val">
                      Rev: {row.currency || 'USD'} {row.homeMarketRevenue ? parseInt(row.homeMarketRevenue, 10).toLocaleString() : 'N/A'}
                    </Box>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Chip
                      label={row.status || 'Pending'}
                      className={getStatusChipClass(row.status)}
                      size="small"
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    <Tooltip title="View Full Plan">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenView(row)}
                        sx={{ color: '#5c4eb4' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Status / Plan">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(row)}
                        sx={{ color: '#0284c7' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDelete(row._id)}
                        sx={{ color: '#ef4444' }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* =========================================================================
          VIEW DETAIL MODAL
      ========================================================================= */}
      <Dialog
        open={viewOpen}
        onClose={handleCloseView}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        {viewItem && (
          <>
            <DialogTitle className="detail-modal-header">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  {viewItem.companyName} · {viewItem.priorityMarket} Expansion
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Reference: <strong>{viewItem.referenceCode || 'N/A'}</strong> · Submitted on{' '}
                  {viewItem.createdAt
                    ? new Date(viewItem.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'N/A'}
                </Typography>
              </Box>
              <Chip
                label={viewItem.status || 'Pending'}
                className={getStatusChipClass(viewItem.status)}
              />
            </DialogTitle>

            <DialogContent dividers className="detail-modal-content">
              {/* SECTION 1: Company Profile */}
              <Typography className="detail-section-title">
                🏢 Step 1: Company Profile
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Founder or Lead Name</Box>
                    <Box className="detail-field-value">{viewItem.founderName || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Work Email</Box>
                    <Box className="detail-field-value">{viewItem.workEmail || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Company Name</Box>
                    <Box className="detail-field-value">{viewItem.companyName || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Website</Box>
                    <Box className="detail-field-value">
                      {viewItem.website ? (
                        <a
                          href={
                            viewItem.website.startsWith('http')
                              ? viewItem.website
                              : `https://${viewItem.website}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#5c4eb4' }}
                        >
                          {viewItem.website}
                        </a>
                      ) : (
                        'Not provided'
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Headquarters</Box>
                    <Box className="detail-field-value">{viewItem.headquarters || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Sector</Box>
                    <Box className="detail-field-value">{viewItem.sector || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Current Stage</Box>
                    <Box className="detail-field-value">{viewItem.stage || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Primary Business Model</Box>
                    <Box className="detail-field-value">{viewItem.businessModel || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Home-Market Annual Revenue</Box>
                    <Box className="detail-field-value">
                      {viewItem.currency || 'USD'}{' '}
                      {viewItem.homeMarketRevenue
                        ? parseInt(viewItem.homeMarketRevenue, 10).toLocaleString()
                        : '0'}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Evidence From Home Market</Box>
                    <Box className="tags-container">
                      {viewItem.homeEvidence && viewItem.homeEvidence.length > 0 ? (
                        viewItem.homeEvidence.map((ev) => (
                          <span key={ev} className="support-tag">
                            ✓ {ev}
                          </span>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          None indicated
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">One-Line Company Description</Box>
                    <Box className="detail-field-value long-text">
                      {viewItem.oneLineDesc || 'N/A'}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* SECTION 2: Target Markets */}
              <Typography className="detail-section-title">
                🌐 Step 2: Target Markets & Strategy
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Priority Target Market</Box>
                    <Box className="detail-field-value" sx={{ color: '#5c4eb4' }}>
                      {viewItem.priorityMarket || 'N/A'}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Additional Markets Under Consideration</Box>
                    <Box className="tags-container">
                      {viewItem.additionalMarkets && viewItem.additionalMarkets.length > 0 ? (
                        viewItem.additionalMarkets.map((m) => (
                          <span key={m} className="support-tag">
                            {m}
                          </span>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          None
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Primary Expansion Objective</Box>
                    <Box className="detail-field-value">{viewItem.expansionObjective || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Desired Entry Timeline</Box>
                    <Box className="detail-field-value">{viewItem.desiredEntryTimeline || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Target Customer Segment</Box>
                    <Box className="detail-field-value">{viewItem.targetCustomer || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Entry Model Being Considered</Box>
                    <Box className="detail-field-value">{viewItem.entryModel || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Why This Market?</Box>
                    <Box className="tags-container">
                      {viewItem.whyThisMarket && viewItem.whyThisMarket.length > 0 ? (
                        viewItem.whyThisMarket.map((r) => (
                          <span key={r} className="support-tag">
                            ★ {r}
                          </span>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          Not specified
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Evidence & Demand Signals</Box>
                    <Box className="detail-field-value long-text">
                      {viewItem.evidenceSignals || 'N/A'}
                    </Box>
                  </Box>
                </Grid>
                {viewItem.marketsToExclude && (
                  <Grid item xs={12}>
                    <Box className="detail-field-box">
                      <Box className="detail-field-label">Markets to Exclude or Avoid</Box>
                      <Box className="detail-field-value long-text">
                        {viewItem.marketsToExclude}
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* SECTION 3: Expansion Readiness */}
              <Typography className="detail-section-title">
                📊 Step 3: Expansion Readiness & Constraints
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Expansion Owner</Box>
                    <Box className="detail-field-value">{viewItem.expansionOwner || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Available Entry Budget</Box>
                    <Box className="detail-field-value" sx={{ color: '#10b981' }}>
                      {viewItem.budgetCurrency || 'USD'}{' '}
                      {viewItem.availableBudget
                        ? parseInt(viewItem.availableBudget, 10).toLocaleString()
                        : '0'}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Current Operating Runway</Box>
                    <Box className="detail-field-value">{viewItem.runwayMonths || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Leadership Capacity</Box>
                    <Box className="detail-field-value">{viewItem.leadershipCapacity || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Product Localisation Required</Box>
                    <Box className="detail-field-value">{viewItem.productLocalisation || 'None'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Local Entity Required</Box>
                    <Box className="detail-field-value">{viewItem.localEntityRequired || 'Unsure'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Preparation Completed</Box>
                    <Box className="tags-container">
                      {viewItem.preparationCompleted && viewItem.preparationCompleted.length > 0 ? (
                        viewItem.preparationCompleted.map((p) => (
                          <span key={p} className="support-tag">
                            ✓ {p}
                          </span>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          None yet
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Capabilities Matrix */}
                {viewItem.capabilities && (
                  <Grid item xs={12}>
                    <Box className="detail-field-box">
                      <Box className="detail-field-label">Internal Capabilities Matrix</Box>
                      <Grid container spacing={1} sx={{ mt: 0.5 }}>
                        {[
                          { key: 'sales', label: 'Sales' },
                          { key: 'marketing', label: 'Marketing' },
                          { key: 'customerSuccess', label: 'Customer Success' },
                          { key: 'operations', label: 'Operations' },
                          { key: 'compliance', label: 'Compliance' },
                        ].map((cap) => {
                          const val = viewItem.capabilities[cap.key] || 'Unassigned';
                          const color =
                            val === 'Ready' ? '#16a34a' : val === 'Partial' ? '#d97706' : '#dc2626';
                          return (
                            <Grid item xs={6} sm={2.4} key={cap.key}>
                              <Box sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 1, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{cap.label}</Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color }}>{val}</Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Biggest Expansion Risk</Box>
                    <Box className="detail-field-value long-text">
                      {viewItem.biggestRisk || 'N/A'}
                    </Box>
                  </Box>
                </Grid>
                {viewItem.nonNegotiables && (
                  <Grid item xs={12}>
                    <Box className="detail-field-box">
                      <Box className="detail-field-label">Non-Negotiable Constraints</Box>
                      <Box className="detail-field-value long-text">
                        {viewItem.nonNegotiables}
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* SECTION 4: Support Requirements */}
              <Typography className="detail-section-title">
                🤝 Step 4: Support Requirements & Execution
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Required Support Workstreams</Box>
                    <Box className="tags-container">
                      {viewItem.supportWorkstreams && viewItem.supportWorkstreams.length > 0 ? (
                        viewItem.supportWorkstreams.map((ws) => (
                          <span key={ws} className="support-tag">
                            🚀 {ws}
                          </span>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          None selected
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Preferred Engagement Format</Box>
                    <Box className="detail-field-value">{viewItem.engagementFormat || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Desired Start Date</Box>
                    <Box className="detail-field-value">{viewItem.desiredStartDate || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Primary Internal Contact</Box>
                    <Box className="detail-field-value">{viewItem.primaryContact || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Decision-Making Speed</Box>
                    <Box className="detail-field-value">{viewItem.decisionSpeed || 'N/A'}</Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Introductions That Matter Most</Box>
                    <Box className="tags-container">
                      {viewItem.introductionsNeeded && viewItem.introductionsNeeded.length > 0 ? (
                        viewItem.introductionsNeeded.map((intro) => (
                          <span key={intro} className="support-tag">
                            🤝 {intro}
                          </span>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          None specified
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">First 90-Day Expected Outcome</Box>
                    <Box className="detail-field-value long-text">
                      {viewItem.first90DayOutcome || 'N/A'}
                    </Box>
                  </Box>
                </Grid>
                {viewItem.anythingElse && (
                  <Grid item xs={12}>
                    <Box className="detail-field-box">
                      <Box className="detail-field-label">Additional Context / Sensitivities</Box>
                      <Box className="detail-field-value long-text">
                        {viewItem.anythingElse}
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* SECTION 5: Documents & Declarations */}
              <Typography className="detail-section-title">
                📑 Step 5: Supporting Materials & Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Attached Documents</Box>
                    {viewItem.documents && viewItem.documents.length > 0 ? (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {viewItem.documents.map((doc, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 1.5,
                              backgroundColor: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}
                          >
                            <span style={{ fontSize: 18 }}>
                              {doc.name && doc.name.toLowerCase().endsWith('.pdf')
                                ? '📄'
                                : '📊'}
                            </span>
                            <Box>
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  fontWeight: 600,
                                  color: '#5c4eb4',
                                  textDecoration: 'none',
                                  fontSize: 13,
                                }}
                              >
                                {doc.name || 'View File'}
                              </a>
                              <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>
                                {doc.docType || 'Document'} · {doc.size || 'Size N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                        No documents uploaded with this submission.
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {viewItem.dataRoomLink && (
                  <Grid item xs={12}>
                    <Box className="detail-field-box">
                      <Box className="detail-field-label">External Data Room Link</Box>
                      <Box className="detail-field-value">
                        <a
                          href={
                            viewItem.dataRoomLink.startsWith('http')
                              ? viewItem.dataRoomLink
                              : `https://${viewItem.dataRoomLink}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#5c4eb4' }}
                        >
                          🔒 {viewItem.dataRoomLink}
                        </a>
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">Accuracy Declaration Confirmed</Box>
                    <Box className="detail-field-value">
                      {viewItem.declarationAccuracy ? '✓ Confirmed' : 'Not confirmed'}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <Box className="detail-field-label">No Guarantee Disclaimer Confirmed</Box>
                    <Box className="detail-field-value">
                      {viewItem.declarationNoGuarantee ? '✓ Confirmed' : 'Not confirmed'}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box className="detail-field-box" sx={{ borderLeft: '4px solid #5c4eb4' }}>
                    <Box className="detail-field-label">Internal Admin Notes</Box>
                    <Box className="detail-field-value long-text">
                      {viewItem.adminNotes || 'No notes added yet.'}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={handleCloseView} sx={{ color: '#64748b' }}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleCloseView();
                  handleOpenEdit(viewItem);
                }}
                className="ge-btn-export"
              >
                Edit Application
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* =========================================================================
          EDIT MODAL
      ========================================================================= */}
      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
      >
        {editItem && (
          <>
            <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>
              Edit Plan: {editItem.companyName}
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              {editError && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: 1,
                    color: '#991b1b',
                    fontSize: 13,
                  }}
                >
                  {editError}
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    size="small"
                    value={editItem.status}
                    onChange={(e) =>
                      setEditItem((prev) => ({ ...prev, status: e.target.value }))
                    }
                  >
                    {STATUS_OPTIONS.map((st) => (
                      <MenuItem key={st} value={st}>
                        {st}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Priority Target Market"
                    size="small"
                    value={editItem.priorityMarket}
                    onChange={(e) =>
                      setEditItem((prev) => ({
                        ...prev,
                        priorityMarket: e.target.value,
                      }))
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Stage"
                    size="small"
                    value={editItem.stage}
                    onChange={(e) =>
                      setEditItem((prev) => ({ ...prev, stage: e.target.value }))
                    }
                  >
                    {STAGE_OPTIONS.map((stg) => (
                      <MenuItem key={stg} value={stg}>
                        {stg}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Sector"
                    size="small"
                    value={editItem.sector}
                    onChange={(e) =>
                      setEditItem((prev) => ({ ...prev, sector: e.target.value }))
                    }
                  >
                    {SECTOR_OPTIONS.map((sec) => (
                      <MenuItem key={sec} value={sec}>
                        {sec}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Expansion Owner"
                    size="small"
                    value={editItem.expansionOwner}
                    onChange={(e) =>
                      setEditItem((prev) => ({
                        ...prev,
                        expansionOwner: e.target.value,
                      }))
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Available Entry Budget"
                    size="small"
                    value={editItem.availableBudget}
                    onChange={(e) =>
                      setEditItem((prev) => ({
                        ...prev,
                        availableBudget: e.target.value,
                      }))
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Expansion Objective"
                    size="small"
                    value={editItem.expansionObjective}
                    onChange={(e) =>
                      setEditItem((prev) => ({
                        ...prev,
                        expansionObjective: e.target.value,
                      }))
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Internal Admin Notes"
                    placeholder="Add team assessment notes, evaluation feedback, contact timeline, etc."
                    value={editItem.adminNotes}
                    onChange={(e) =>
                      setEditItem((prev) => ({
                        ...prev,
                        adminNotes: e.target.value,
                      }))
                    }
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseEdit} sx={{ color: '#64748b' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                disabled={editLoading}
                className="ge-btn-export"
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* =========================================================================
          DELETE CONFIRMATION MODAL
      ========================================================================= */}
      <Dialog open={deleteOpen} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>
          Delete Plan Submission?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Are you sure you want to permanently delete this Global Expansion plan? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDelete} sx={{ color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GlobalExpansionAdminView;
